/**
 * Fetch dashboard observations from the Lambda GET endpoint.
 *
 * Requires a Google ID token (passed in Authorization: Bearer <token>).
 * The Lambda verifies the token, filters rows by observer email for
 * non-admins, and returns the user record so the UI can show admin
 * affordances.
 */

import type { DashboardObservation } from './types';

const SYNC_URL = import.meta.env.VITE_SYNC_URL as string | undefined;

export interface DashboardUser {
  email: string;
  name?: string;
  picture?: string;
  isAdmin: boolean;
}

export interface FetchResult {
  ok: boolean;
  observations: DashboardObservation[];
  fetchedAt: string;
  user?: DashboardUser;
  error?: string;
  /** Set when the failure is auth-related so the UI can prompt re-login. */
  authError?: boolean;
}

export async function fetchDashboardData(idToken: string): Promise<FetchResult> {
  if (!SYNC_URL) {
    return {
      ok: false,
      observations: [],
      fetchedAt: new Date().toISOString(),
      error: 'Dashboard sync URL is not configured.',
    };
  }

  try {
    const resp = await fetch(SYNC_URL, {
      method: 'GET',
      headers: { Authorization: `Bearer ${idToken}` },
    });

    if (resp.status === 401) {
      return {
        ok: false,
        observations: [],
        fetchedAt: new Date().toISOString(),
        error: 'Your session has expired. Please sign in again.',
        authError: true,
      };
    }

    if (!resp.ok) {
      const text = await resp.text().catch(() => '');
      return {
        ok: false,
        observations: [],
        fetchedAt: new Date().toISOString(),
        error: `Lambda returned ${resp.status}: ${text}`,
      };
    }

    const json = (await resp.json()) as FetchResult;
    return json;
  } catch (err) {
    return {
      ok: false,
      observations: [],
      fetchedAt: new Date().toISOString(),
      error: err instanceof Error ? err.message : 'Network error',
    };
  }
}
